import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { User } from '../../hooks/useUsers'
import type { Device } from '../../hooks/useDevices'
import type { DriverStatusBeacon } from '../../hooks/useWorkforce'

interface Props {
  users: User[]
  devices: Device[]
  beacons: DriverStatusBeacon[]
  draggingJobId: string | null
  onDropJob: (jobId: string, driverId: string) => Promise<void>
}

/// Wave 1 E1.2 — Drivers panel.
///
/// Bottom strip of horizontally-scrollable driver chips. Each chip is a
/// drop target for a job card from the JobQueue (assign by drag-drop).
/// Chip colour reflects the latest broadcast beacon (en_route, arrived,
/// loading, …) and falls back to a "no recent beacon" grey state.
export function DriversPanel({ users, beacons, draggingJobId, onDropJob }: Props) {
  const { t } = useTranslation()
  const beaconByDriver = useMemo(() => {
    const m = new Map<string, DriverStatusBeacon>()
    for (const b of beacons) m.set(b.driver_user_id, b)
    return m
  }, [beacons])

  return (
    <div className="border-t border-slate-200 bg-white px-3 py-2 overflow-x-auto">
      <div className="flex items-center gap-2 min-w-max">
        <span className="text-xs uppercase tracking-wide text-slate-500 mr-2">
          {t('dispatch.crew', 'Crew')}
        </span>
        {users.map((u) => {
          const b = beaconByDriver.get(u.id)
          return (
            <DriverChip
              key={u.id}
              user={u}
              beacon={b}
              isDropTarget={draggingJobId !== null}
              onDrop={(jobId) => onDropJob(jobId, u.id)}
            />
          )
        })}
      </div>
    </div>
  )
}

function DriverChip({
  user,
  beacon,
  isDropTarget,
  onDrop,
}: {
  user: User
  beacon: DriverStatusBeacon | undefined
  isDropTarget: boolean
  onDrop: (jobId: string) => Promise<void>
}) {
  const [hover, setHover] = useState(false)
  const colors = beaconColors(beacon?.beacon)

  return (
    <div
      onDragOver={(e) => {
        if (isDropTarget) {
          e.preventDefault()
          setHover(true)
        }
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault()
        const jobId = e.dataTransfer.getData('text/plain')
        setHover(false)
        if (jobId) void onDrop(jobId)
      }}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-default text-xs whitespace-nowrap ${
        hover ? 'ring-2 ring-sky-300 bg-sky-50' : ''
      } ${colors.bg} ${colors.text} ${colors.border}`}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${colors.dot}`} />
      <span className="font-medium">{user.name}</span>
      {beacon ? (
        <span className="opacity-70">· {beacon.beacon}</span>
      ) : (
        <span className="opacity-50">· offline</span>
      )}
    </div>
  )
}

function beaconColors(beacon: string | undefined): {
  bg: string
  text: string
  border: string
  dot: string
} {
  switch (beacon) {
    case 'en_route':
      return { bg: 'bg-sky-50', text: 'text-sky-800', border: 'border-sky-200', dot: 'bg-sky-500' }
    case 'arrived':
      return { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200', dot: 'bg-emerald-500' }
    case 'loading':
    case 'unloading':
      return { bg: 'bg-violet-50', text: 'text-violet-800', border: 'border-violet-200', dot: 'bg-violet-500' }
    case 'delayed':
      return { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200', dot: 'bg-amber-500' }
    case 'break':
      return { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-200', dot: 'bg-yellow-500' }
    default:
      return { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', dot: 'bg-slate-400' }
  }
}
